import { useEffect, useState, useContext, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import * as XLSX from "xlsx";
import { getProducts } from "../../api/productApi";
import { getLanguages } from "../../api/languageApi";
import { createTranslation } from "../../api/translationApi";
import { AuthContext } from "../../Context/AuthContext";

export default function TranslationForm({ visible, onHide, onSaved }) {
  const { auth } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;

    setPreview([]);
    setErrors([]);

    Promise.all([
      getProducts(auth.token),
      getLanguages(auth.token),
    ]).then(([p, l]) => {
      setProducts(p.data.data || []);
      setLanguages((l.data.data || []).filter(x => x.is_enabled));
    });
  }, [visible, auth.token]);

  const duplicateMap = useMemo(() => {
    const map = {};
    preview.forEach(r => {
      const key = `${r.product_id ?? "null"}-${r.source_key}-${r.language_id}`;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [preview]);

  const handleFile = async (event) => {
    const file = event.files?.[0];
    if (!file) return;

    let data = [];
    try {
      if (file.name.endsWith(".json")) {
        data = JSON.parse(await file.text());
      } else {
        const wb = XLSX.read(await file.arrayBuffer());
        data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      }
    } catch {
      alert("Invalid file format");
      return;
    }

    const parsed = [];
    const err = [];

    data.forEach((r, i) => {
      const productName = String(r.product || "").trim();
      const source_key = String(r.source || "").trim();
      const langCode = String(r.language || "").trim().toLowerCase();

      if (!source_key || !langCode) {
        err.push(`Row ${i + 1}: source & language required`);
        return;
      }

      const product = productName
        ? products.find(p => p.name === productName)
        : null;

      if (productName && !product) {
        err.push(`Row ${i + 1}: invalid product "${productName}"`);
        return;
      }

      const lang = languages.find(l => l.code === langCode);
      if (!lang) {
        err.push(`Row ${i + 1}: invalid language "${langCode}"`);
        return;
      }

      parsed.push({
        product_id: product?.id ?? null,
        product_name: product?.name ?? "",
        source_key,
        language_id: lang.id,
        language_code: lang.code,
        translation: r.translation ?? "",
      });
    });

    setPreview(parsed);
    setErrors(err);
  };

  const handleDeleteRow = (index) => {
    const copy = [...preview];
    copy.splice(index, 1);
    setPreview(copy);
  };

  const handleUpload = () => {
    if (!preview.length) return alert("No data to upload");
    if (errors.length) return alert(errors.join("\n"));

    const hasDup = Object.values(duplicateMap).some(v => v > 1);

    confirmDialog({
      header: "Confirm Upload",
      icon: "pi pi-exclamation-triangle",
      message: hasDup
        ? "Duplicate product + source + language found. Existing translations will be UPDATED. Continue?"
        : "Upload translations?",
      accept: async () => {
        try {
          setIsLoading(true);
          for (const row of preview) {
            await createTranslation(
              {
                product_id: row.product_id,
                source_key: row.source_key,
                language_id: row.language_id,
                translation: row.translation || null,
              },
              auth.token
            );
          }
          onSaved?.();
          onHide();
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <Dialog
      header="Upload Translations"
      visible={visible}
      onHide={onHide}
      modal
      className="w-8"
    >
      <ConfirmDialog />

      <div className="p-3 flex flex-column gap-3">

        {preview.length === 0 && (
        <div className="flex justify-content-center">
          <FileUpload
            mode="basic"
            accept=".json,.xlsx,.xls"
            chooseLabel="Upload"
            auto
            onSelect={handleFile}
            chooseOptions={{
              icon: "pi pi-upload",
              style: {
                backgroundColor: "#d87347ff",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "0.5rem 1rem",
                cursor: "pointer",
              },
            }}
            style={{
              border: "2px dashed #bebbbaff",
              borderRadius: 8,
              padding: 50,
              background: "#f8f9fa",
            }}
          />
        </div>
        )}

        {errors.length > 0 && (
          <div style={{
            background: "#ffe6e6",
            border: "1px solid #f5c6cb",
            borderRadius: 8,
            padding: 10,
            maxHeight: 120,
            overflow: "auto",
          }}>
            <b>Errors:</b>
            <ul className="m-0 pl-3">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ maxHeight: 320, overflow: "auto" }}>
            <table className="p-datatable-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Source</th>
                  <th>Language</th>
                  <th>Translation</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr
                    key={i}
                    style={{
                      background:
                        duplicateMap[`${r.product_id ?? "null"}-${r.source_key}-${r.language_id}`] > 1
                          ? "#ffe6e6"
                          : "",
                    }}
                  >
                    <td>
                      <Dropdown
                        value={r.product_id}
                        options={products}
                        optionLabel="name"
                        optionValue="id"
                        placeholder="(No Product)"
                        editable
                        showClear
                        className="w-full"
                        onChange={e => {
                          const copy = [...preview];
                          const prod = products.find(p => p.id === e.value);
                          copy[i].product_id = e.value ?? null;
                          copy[i].product_name = prod?.name ?? "";
                          setPreview(copy);
                        }}
                      />
                    </td>

                    <td>
                      <InputText
                        value={r.source_key}
                        className="w-full"
                        onChange={e => {
                          const copy = [...preview];
                          copy[i].source_key = e.target.value;
                          setPreview(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Dropdown
                        value={r.language_id}
                        options={languages}
                        optionLabel="code"
                        optionValue="id"
                        editable
                        className="w-full"
                        onChange={e => {
                          const copy = [...preview];
                          copy[i].language_id = e.value;
                          setPreview(copy);
                        }}
                      />
                    </td>

                    <td>
                      <InputText
                        value={r.translation}
                        className="w-full"
                        onChange={e => {
                          const copy = [...preview];
                          copy[i].translation = e.target.value;
                          setPreview(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-text"
                        onClick={() => handleDeleteRow(i)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Button
          label={isLoading ? "Uploading..." : "Upload"}
          icon={isLoading ? "pi pi-spin pi-spinner" : ""}
          onClick={handleUpload}
          disabled={isLoading}
          className="p-button-block"
          style={{ backgroundColor: "#FC5F1CFF", color: "#fff" }}
        />
      </div>
    </Dialog>
  );
}
